import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import {
  emptyProfile,
  MAX_PROFILES_PER_CASE,
  type CaseCandidateRef,
  type CaseEntryKind,
  type CaseFileMeta,
  type CaseProfile,
  type CaseProfileMode,
  type FileKind,
} from './types';

export interface CaseSubmissionReceipt {
  createdCaseIds: number[];
  createdCount: number;
  submittedAt: string;
  signatureDataUrl: string | null;
}

interface CaseBuilderState {
  pricingConfirmed: boolean;
  otpVerified: boolean;
  profiles: CaseProfile[];
  signatureDataUrl: string | null;
  declarationAccepted: boolean;
  receipt: CaseSubmissionReceipt | null;

  setPricing: (v: boolean) => void;
  setOtp: (v: boolean) => void;
  addProfile: () => void;
  removeProfile: (id: string) => void;
  updateProfile: (id: string, updater: (p: CaseProfile) => CaseProfile) => void;
  setProfileFile: (id: string, kind: FileKind, meta: CaseFileMeta | null) => void;
  /** Switch a card between picking a Tally candidate and typing one in. */
  setProfileMode: (id: string, mode: CaseProfileMode) => void;
  /** Pick (or clear) the on-platform candidate; clears any prior selection. */
  setProfileCandidate: (id: string, candidate: CaseCandidateRef | null) => void;
  toggleProfileEntry: (id: string, kind: CaseEntryKind, entryId: number) => void;
  setSignature: (dataUrl: string | null) => void;
  setDeclaration: (v: boolean) => void;
  setReceipt: (r: CaseSubmissionReceipt | null) => void;
  /** Clear the transient case-builder inputs after a successful submission
   * while keeping the receipt, so the next request starts with a blank pad and
   * a fresh profile instead of the just-submitted ones. */
  finishSubmission: () => void;
  startFresh: () => void;
  reset: () => void;
}

function newId(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // Fallback for environments without crypto.randomUUID.
    return `p-${Date.now()}-${(globalThis.performance?.now?.() ?? 0).toFixed(0)}`;
  }
}

const FILE_FIELD: Record<FileKind, 'resume_file' | 'employee_consent_file'> = {
  employee_resume: 'resume_file',
  employee_consent: 'employee_consent_file',
};

const base = {
  pricingConfirmed: false,
  otpVerified: false,
  signatureDataUrl: null as string | null,
  declarationAccepted: false,
  receipt: null as CaseSubmissionReceipt | null,
};

export const useCaseBuilderStore = create<CaseBuilderState>()(
  persist(
    (set) => ({
      ...base,
      profiles: [emptyProfile(newId())],

      setPricing: (v) => set({ pricingConfirmed: v }),
      setOtp: (v) => set({ otpVerified: v }),
      addProfile: () =>
        set((s) =>
          s.profiles.length >= MAX_PROFILES_PER_CASE
            ? s
            : { profiles: [...s.profiles, emptyProfile(newId())] },
        ),
      removeProfile: (id) =>
        set((s) => ({ profiles: s.profiles.filter((p) => p.profile_id !== id) })),
      updateProfile: (id, updater) =>
        set((s) => ({
          profiles: s.profiles.map((p) => (p.profile_id === id ? updater(p) : p)),
        })),
      setProfileFile: (id, kind, meta) =>
        set((s) => ({
          profiles: s.profiles.map((p) =>
            p.profile_id === id ? { ...p, [FILE_FIELD[kind]]: meta } : p,
          ),
        })),
      setProfileMode: (id, mode) =>
        set((s) => ({
          profiles: s.profiles.map((p) => (p.profile_id === id ? { ...p, mode } : p)),
        })),
      setProfileCandidate: (id, candidate) =>
        set((s) => ({
          profiles: s.profiles.map((p) =>
            p.profile_id === id
              ? {
                  ...p,
                  candidate,
                  // Record ids belong to one candidate; swapping the candidate
                  // must not carry the previous one's selection over.
                  selected_entries: { education: [], work_experience: [], certificates: [] },
                }
              : p,
          ),
        })),
      toggleProfileEntry: (id, kind, entryId) =>
        set((s) => ({
          profiles: s.profiles.map((p) => {
            if (p.profile_id !== id) return p;
            const current = p.selected_entries[kind];
            const next = current.includes(entryId)
              ? current.filter((value) => value !== entryId)
              : [...current, entryId];
            return { ...p, selected_entries: { ...p.selected_entries, [kind]: next } };
          }),
        })),
      setSignature: (dataUrl) => set({ signatureDataUrl: dataUrl }),
      setDeclaration: (v) => set({ declarationAccepted: v }),
      setReceipt: (r) => set({ receipt: r }),
      finishSubmission: () =>
        set({
          pricingConfirmed: false,
          otpVerified: false,
          signatureDataUrl: null,
          declarationAccepted: false,
          profiles: [emptyProfile(newId())],
          // receipt is intentionally preserved.
        }),
      startFresh: () => set({ ...base, profiles: [emptyProfile(newId())] }),
      reset: () => set({ ...base, profiles: [emptyProfile(newId())] }),
    }),
    {
      name: 'tally:employer-case-builder',
      storage: createJSONStorage(() => sessionStorage),
    },
  ),
);
